import { useLocation, useNavigate } from "react-router-dom";
import { Menu } from "@headlessui/react";
import { DuplicateIcon, TrashIcon, DotsVerticalIcon } from "@heroicons/react/outline";
import { useModal } from "effects";
import { showPopupFlag } from "lib/actions/userInterface";
import handlePopupFlagError from "lib/errors/handlePopupFlagError";
import { useDispatch, useSelector } from "store";
import { deleteStorage, duplicateStorage, selectStorageByID } from "store/reducers";
import { TransparentButton } from "components/shared/buttons";
import { ConfirmModal } from "components/shared/generic/modals/ConfirmModal/ConfirmModal";
import { DropdownMenu, DropdownMenuItemAction } from "components/shared/menus";

type StorageTableRowDropdownProps = {
  storageId: string;
  onDelete?: () => void;
};

export const StoragesTableRowDropdown = ({ storageId, onDelete }: StorageTableRowDropdownProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname: url } = useLocation();
  const { openModal } = useModal();
  const storage = useSelector(selectStorageByID(storageId));

  if (!storage) {
    return null;
  }

  const removeStorage = () => {
    openModal(
      <ConfirmModal
        actionText="Archive"
        description={`Are you sure you want to archive this ${storage.type === "toolbox" ? "toolbox" : "storage"}?`}
        onConfirm={async () => {
          try {
            await dispatch(deleteStorage(storageId)).unwrap();
            dispatch(
              showPopupFlag({
                appearance: "success",
                title: `${storage.type === "toolbox" ? "Toolbox" : "Storage"} was removed`,
              }),
            );
            onDelete && onDelete();
          } catch (err) {
            handlePopupFlagError(err);
          }
        }}
      />,
    );
  };

  const duplicate = () => {
    openModal(
      <ConfirmModal
        actionText="Duplicate"
        description={`Are you sure you want to duplicate this ${storage.type === "toolbox" ? "toolbox" : "storage"}?`}
        onConfirm={async () => {
          try {
            const newStorage = await dispatch(
              duplicateStorage({
                storageId,
              }),
            ).unwrap();
            navigate(`${url}/${newStorage.id}`);
          } catch (err) {
            handlePopupFlagError(err);
          }
        }}
      />,
    );
  };

  const actions: DropdownMenuItemAction[][] = [
    [
      {
        label: "Duplicate",
        icon: DuplicateIcon,
        onClick: duplicate,
      },
      {
        label: "Archive",
        icon: TrashIcon,
        onClick: removeStorage,
      },
    ],
  ];

  return (
    <DropdownMenu items={actions}>
      <Menu.Button as={TransparentButton} trailingIcon={DotsVerticalIcon} padding="px-2" />
    </DropdownMenu>
  );
};
